import { useState } from "react";
import { Input } from "./ui/input";
import { COUNTRIES } from "../data/countries";
import { VirtualSelect } from "./virtual-select";

const parsePhoneValue = (value: string | undefined) => {
  if (!value) {
    return {
      country: COUNTRIES[0],
      phoneNumber: "",
    };
  }

  const country =
    COUNTRIES.find((c) => value.startsWith(c.dial_code)) || COUNTRIES[0];
  const phoneNumber = value.replace(country.dial_code, "").trim();

  return { country, phoneNumber };
};

const getDefaultCountry = (
  tz: string = Intl.DateTimeFormat().resolvedOptions().timeZone
) => {
  const country =
    typeof window !== "undefined" ? window.DS_PAGE?.country : undefined;

  // 1. Use suggestion if valid
  const bySuggestion = COUNTRIES.find((c) => c.code === country);
  if (bySuggestion) return bySuggestion;

  // 2. Use timezone if mapped
  const byTimezone = COUNTRIES.find((c) => c.timezone === tz);
  if (byTimezone) return byTimezone;

  // 3. Fallback to India
  return COUNTRIES.find((c) => c.code === "IN")!;
};

interface PhoneInputProps {
  value?: string;
  onChange: (value: string) => void;
  required: boolean;
  modalPopover?: boolean;
  className?: string;
}

export const PhoneInput = ({
  value,
  onChange,
  required,
  modalPopover,
  className,
}: PhoneInputProps) => {
  const { country, phoneNumber } = parsePhoneValue(value);
  const [selectedCountry, setSelectedCountry] = useState(
    value && country ? country : getDefaultCountry()
  );

  const [localPhoneNumber, setLocalPhoneNumber] = useState<string>(phoneNumber);

  const handlePhoneChange = (value: string) => {
    setLocalPhoneNumber(value);
    onChange(`${selectedCountry.dial_code} ${value}`);
  };

  const handleCountryChange = (dialCode: string) => {
    const country = COUNTRIES.find((c) => c.dial_code === dialCode);
    if (country) {
      setSelectedCountry(country);
      onChange(`${country.dial_code} ${localPhoneNumber}`);
    }
  };

  return (
    <div className="flex gap-2">
      <VirtualSelect
        options={COUNTRIES.map((c) => ({
          label: `${c.name} (${c.dial_code})`,
          value: c.dial_code,
        }))}
        defaultValue={selectedCountry ? selectedCountry.dial_code : ""}
        onValueChange={handleCountryChange}
        placeholder="Country code"
        width="140px"
        modalPopover={modalPopover}
        display="value"
      ></VirtualSelect>
      <Input
        type="tel"
        value={phoneNumber}
        onChange={(e) => handlePhoneChange(e.target.value)}
        required={required}
        className={className}
      />
    </div>
  );
};
